import React from "react";
import { Image as ImageIcon, Upload, Trash2, Mail, CheckCircle2, AlertTriangle, Info, ExternalLink, Key, Eye, EyeOff, RefreshCw, Copy, Lock } from "lucide-react";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import BulkImportCard from "../../components/BulkImportCard";
import { toast } from "sonner";

function WishlinkLogoUploader({ value, hasLogo, onChange }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Please upload an image"); return; }
    if (file.size > 256 * 1024) { toast.error(`Image is ${Math.round(file.size/1024)} KB — max 256 KB. Compress or resize.`); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/settings/wishlink-logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Wishlink logo saved (${Math.round(data.size / 1024)} KB) — persists across deploys.`);
      await onChange();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const clear = async () => {
    if (!window.confirm("Remove the Wishlink logo?")) return;
    setBusy(true);
    try {
      await api.put("/settings", { wishlink_logo_url: null });
      toast.success("Logo removed");
      await onChange();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Failed to remove logo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-5" data-testid="wishlink-logo-uploader">
      <div className="h-20 w-48 rounded-xl border border-[var(--border-default)] flex items-center justify-center p-3 shrink-0" style={{ background: "var(--bg-page)" }}>
        {hasLogo && value ? (
          <img src={value} alt="Wishlink" className="max-h-full max-w-full object-contain" draggable={false} />
        ) : (
          <span className="text-[11px] uppercase tracking-wider text-[var(--text-secondary)]">No logo set</span>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <input type="file" accept="image/*" ref={inputRef} onChange={onFile} className="hidden" data-testid="wishlink-logo-input" />
        <button onClick={() => inputRef.current?.click()} disabled={busy} className="wl-btn-primary inline-flex items-center gap-1.5 text-xs disabled:opacity-50" data-testid="wishlink-logo-upload-btn">
          <Upload size={12} /> {busy ? "Uploading…" : hasLogo ? "Replace logo" : "Upload Wishlink logo"}
        </button>
        {hasLogo && (
          <button onClick={clear} disabled={busy} className="wl-btn-ghost inline-flex items-center gap-1.5 text-xs text-red-700 hover:text-red-800 disabled:opacity-50" data-testid="wishlink-logo-clear-btn">
            <Trash2 size={11} /> Remove
          </button>
        )}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { refresh: refreshSettings, settings, wishlinkLogoUrl } = useSettings();
  const [emailStatus, setEmailStatus] = React.useState(null);
  React.useEffect(() => {
    api.get("/notify/status").then((r) => setEmailStatus(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-settings-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1">Workspace-wide branding and preferences.</p>
      </div>

      <div className="wl-card-solid p-6 mb-6 max-w-3xl" data-testid="branding-card">
        <div className="flex items-center gap-2 mb-1.5">
          <ImageIcon size={16} className="text-[var(--brand)]" />
          <h2 className="font-display text-base font-semibold">Wishlink branding</h2>
        </div>
        <p className="text-sm text-[var(--text-secondary)] mb-1">
          Used on the admin sidebar, login page and tracker header. Recommended: a horizontal PNG (transparent or white background — we'll erase the white).
        </p>
        <p className="text-xs text-[var(--text-secondary)] mb-5">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">Persists across deploys</span>
          {" "}— stored directly in the database (max 256 KB).
        </p>
        <WishlinkLogoUploader value={wishlinkLogoUrl || settings?.wishlink_logo_url || ""} hasLogo={!!settings?.wishlink_logo_url} onChange={refreshSettings} />
      </div>

      <div className="wl-card-solid p-6 mb-6 max-w-3xl" data-testid="email-status-card">
        <div className="flex items-center gap-2 mb-1.5">
          <Mail size={16} className="text-[var(--brand)]" />
          <h2 className="font-display text-base font-semibold">Transactional emails</h2>
        </div>
        <p className="text-sm text-[var(--text-secondary)] mb-4">
          Used for invites, password resets, and brand notifications. Active provider shown below.
        </p>
        {emailStatus ? (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2.5">
                {emailStatus.configured ? (
                  <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                )}
                <div>
                  <div className="font-medium" data-testid="email-provider-label">
                    {emailStatus.provider === "ses" && "Amazon SES connected"}
                    {emailStatus.provider === "brevo" && "Brevo connected"}
                    {emailStatus.provider === "stub" && "Not configured (stub mode)"}
                    {!emailStatus.provider && (emailStatus.configured ? "Connected" : "Not configured (stub mode)")}
                  </div>
                  <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                    {emailStatus.provider === "ses" && (
                      <>Emails dispatched via Amazon SES{emailStatus.aws_region ? ` (${emailStatus.aws_region})` : ""}. If your SES account is still in sandbox, recipient addresses must also be verified — request <a className="underline text-[var(--brand)]" href="https://docs.aws.amazon.com/ses/latest/dg/request-production-access.html" target="_blank" rel="noreferrer">production access</a> to send to anyone.</>
                    )}
                    {emailStatus.provider === "brevo" && "Emails dispatched via Brevo. Add AWS keys to switch to SES."}
                    {emailStatus.provider === "stub" && "Emails are logged to backend logs but not delivered. Add AWS SES keys (or BREVO_API_KEY) to enable."}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-[var(--text-secondary)] mb-1">Current sender (live)</div>
                <div className="space-y-1" data-testid="email-sender-live">
                  <div>
                    <span className="text-[11px] uppercase tracking-wider text-[var(--text-secondary)]">From name</span>
                    <div className="font-mono text-xs break-all" data-testid="email-sender-name">{emailStatus.sender_name}</div>
                  </div>
                  <div>
                    <span className="text-[11px] uppercase tracking-wider text-[var(--text-secondary)]">From address</span>
                    <div className="font-mono text-xs break-all" data-testid="email-sender-email">{emailStatus.sender_email}</div>
                  </div>
                </div>
              </div>
            </div>

            <details className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface-warm)]" data-testid="email-sender-help">
              <summary className="cursor-pointer px-3.5 py-2.5 text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)] flex items-center gap-2">
                <Info size={13} /> How to configure / switch providers
              </summary>
              <div className="px-3.5 pb-3.5 pt-1 text-xs text-[var(--text-primary)] leading-relaxed space-y-2">
                <p>The backend tries <strong>Amazon SES first</strong> (if AWS keys are set), then falls back to <strong>Brevo</strong>, then stub mode. Set these env vars in <span className="font-mono">backend/.env</span> on the deployed environment:</p>
                <pre className="p-2 rounded bg-white border border-[var(--border-default)] font-mono text-[11px] overflow-x-auto">{`# Sender (shared across providers)
EMAIL_SENDER_EMAIL=noreply-kiwi@wishlink.com
EMAIL_SENDER_NAME=Wishlink Kiwi

# Amazon SES (preferred)
AWS_ACCESS_KEY_ID=AKIA...
AWS_SECRET_ACCESS_KEY=...
AWS_REGION=ap-south-1

# Brevo (fallback)
BREVO_API_KEY=xkeysib-...`}</pre>
                <ol className="list-decimal pl-4 space-y-1.5">
                  <li>For SES: verify <span className="font-mono">noreply-kiwi@wishlink.com</span> (or the whole <span className="font-mono">wishlink.com</span> domain) under SES → Identities in the same region as <span className="font-mono">AWS_REGION</span>.</li>
                  <li>If your SES account is still in <strong>sandbox</strong>, verify each recipient too — or <a className="underline text-[var(--brand)] inline-flex items-center gap-0.5" href="https://docs.aws.amazon.com/ses/latest/dg/request-production-access.html" target="_blank" rel="noreferrer">request production access <ExternalLink size={10} /></a>.</li>
                  <li>Use an IAM user scoped to <span className="font-mono">ses:SendEmail</span> + <span className="font-mono">ses:SendRawEmail</span> only — never your root keys.</li>
                  <li>Redeploy (or restart the backend in preview). This card will then show the new provider.</li>
                </ol>
              </div>
            </details>
          </div>
        ) : (
          <div className="text-xs text-[var(--text-secondary)]">Loading…</div>
        )}
      </div>

      <SheetsAuthCard />

      <VrTokenCard />

      <BulkImportCard />
    </div>
  );
}

// Admin-only card to manage the Google Sheets service-account credentials. Paste
// the full JSON from the downloaded key file; the backend validates it by minting
// a token before persisting. The card never shows the private key — only the
// client_email + project_id so admins can verify which SA is configured.
function SheetsAuthCard() {
  const [info, setInfo] = React.useState(null);
  const [jsonText, setJsonText] = React.useState("");
  const [showInput, setShowInput] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try { const { data } = await api.get("/admin/sheets-auth"); setInfo(data); }
    catch (e) { console.error("[sheets-auth] load", e); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const submit = async () => {
    let parsed;
    try { parsed = JSON.parse(jsonText); }
    catch { toast.error("That's not valid JSON. Paste the entire contents of the .json key file."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sheets-auth", { json_body: parsed });
      setInfo({ configured: true, ...data });
      setJsonText("");
      setShowInput(false);
      toast.success("Service account connected.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed.");
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sheets-auth/test");
      toast.success(`Connection OK · ${data.client_email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed.");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove the service account? Sheets without public access will stop loading until you re-share them or paste new credentials.")) return;
    setBusy(true);
    try {
      await api.delete("/admin/sheets-auth");
      setInfo({ configured: false });
      toast.success("Service account removed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed.");
    } finally { setBusy(false); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Couldn't copy — select manually."); }
  };

  return (
    <div className="wl-card-solid p-6 mt-6" data-testid="sheets-auth-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--brand-tint)", color: "var(--brand)" }}>
          <Lock size={18} />
        </div>
        <div>
          <div className="font-display text-lg font-semibold">Google Sheets access</div>
          <div className="text-xs text-[var(--text-secondary)] mt-0.5">Authenticate with a service account so campaign sheets can be private (not "anyone with the link").</div>
        </div>
      </div>

      {!info ? (
        <div className="text-xs text-[var(--text-secondary)]">Loading…</div>
      ) : info.configured ? (
        <div className="space-y-3">
          <div className="flex items-start gap-2.5">
            <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-sm">Connected · sheets fetched via authenticated API.</div>
              <div className="text-xs text-[var(--text-secondary)] mt-1">Share each campaign sheet (Viewer access) with the email below, then turn off "anyone with the link".</div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[var(--text-secondary)] mb-1">Service account email</div>
              <div className="flex items-center gap-1.5">
                <code className="font-mono text-xs px-2 py-1.5 rounded bg-[var(--bg-surface-warm)] border border-[var(--border-default)] flex-1 truncate" data-testid="sa-email" title={info.client_email}>{info.client_email}</code>
                <button onClick={() => copy(info.client_email)} className="wl-btn-secondary text-xs inline-flex items-center gap-1" data-testid="sa-email-copy"><Copy size={12} /></button>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[var(--text-secondary)] mb-1">Project</div>
              <code className="font-mono text-xs px-2 py-1.5 rounded bg-[var(--bg-surface-warm)] border border-[var(--border-default)] block truncate">{info.project_id}</code>
            </div>
          </div>
          {info.updated_at && (
            <div className="text-[11px] text-[var(--text-secondary)]">Updated · {new Date(info.updated_at).toLocaleString()}</div>
          )}
          <div className="flex items-center gap-2 pt-2">
            <button onClick={test} disabled={busy} className="wl-btn-secondary text-xs inline-flex items-center gap-1.5" data-testid="sa-test"><CheckCircle2 size={12} /> Test connection</button>
            <button onClick={() => setShowInput((v) => !v)} className="wl-btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="sa-replace"><RefreshCw size={12} /> Replace</button>
            <button onClick={remove} disabled={busy} className="wl-btn-ghost text-xs inline-flex items-center gap-1.5 ml-auto text-[var(--brand)]" data-testid="sa-remove"><Trash2 size={12} /> Remove</button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2.5">
          <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-sm">No service account configured.</div>
            <div className="text-xs text-[var(--text-secondary)] mt-1">Sheets currently load via the public CSV path — they must be shared as "anyone with the link". Paste a service-account JSON to lock this down.</div>
            <button onClick={() => setShowInput(true)} className="wl-btn-primary text-xs inline-flex items-center gap-1.5 mt-3" data-testid="sa-add">Paste service account JSON</button>
          </div>
        </div>
      )}

      {showInput && (
        <div className="mt-4 space-y-2">
          <label className="text-xs uppercase tracking-wide text-[var(--text-secondary)]">Service account JSON</label>
          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            placeholder='Paste the entire contents of the .json file you downloaded from Google Cloud — e.g. { "type": "service_account", "project_id": "...", "private_key_id": "...", "private_key": "-----BEGIN PRIVATE KEY-----\n...", "client_email": "wishlink-sheets-reader@...iam.gserviceaccount.com", ... }'
            className="wl-input font-mono text-[11px] w-full"
            rows={8}
            data-testid="sa-json-input"
          />
          <div className="flex gap-2">
            <button onClick={submit} disabled={busy || !jsonText.trim()} className="wl-btn-primary text-xs" data-testid="sa-save">{busy ? "Validating…" : "Save & validate"}</button>
            <button onClick={() => { setShowInput(false); setJsonText(""); }} className="wl-btn-ghost text-xs">Cancel</button>
          </div>
          <div className="text-[11px] text-[var(--text-secondary)] mt-1">Validates by minting a Google access token before saving — if Google rejects the credentials, nothing is persisted.</div>
        </div>
      )}

      <details className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface-warm)] mt-4">
        <summary className="cursor-pointer px-3.5 py-2.5 text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)] flex items-center gap-2">
          <Info size={13} /> How sheet sharing works after this
        </summary>
        <div className="px-3.5 pb-3.5 pt-1 text-xs text-[var(--text-primary)] leading-relaxed space-y-2">
          <ol className="list-decimal pl-4 space-y-1">
            <li>Paste the SA JSON above and save.</li>
            <li>For each campaign sheet: Share → paste the SA email → set "Viewer" → uncheck "Notify".</li>
            <li>Confirm tracking loads for that campaign.</li>
            <li>In the same Share dialog, change "General access" → "Restricted" (turns off "anyone with link").</li>
          </ol>
          <p>If a sheet isn't shared with the SA, the tracker shows an inline error with a copy-able share hint.</p>
        </div>
      </details>
    </div>
  );
}

// Admin-only card to view + rotate the shared bearer token used by the external
// Value Realisation pipeline (Apps Script). The token is generated lazily by the
// backend and stored in Mongo so it survives backend restarts and can be rotated
// from this UI without a redeploy.
function VrTokenCard() {
  const [info, setInfo] = React.useState(null);  // {token, masked, rotated_at}
  const [revealed, setRevealed] = React.useState(null);  // null | full-token-string
  const [busy, setBusy] = React.useState(false);

  const loadMasked = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/vr-token");
      setInfo(data);
    } catch (e) { console.error("[vr-token] load failed", e); }
  }, []);

  React.useEffect(() => { loadMasked(); }, [loadMasked]);

  const reveal = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/vr-token?reveal=1");
      setRevealed(data.token);
    } catch (e) {
      toast.error("Couldn't reveal token");
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    if (!window.confirm("Rotating the token will immediately break the existing Apps Script integration until you paste the new token into Apps Script Properties. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/vr-token/regenerate");
      setRevealed(data.token);
      await loadMasked();
      toast.success("New token generated — copy it now.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotation failed");
    } finally { setBusy(false); }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Token copied to clipboard");
    } catch {
      toast.error("Couldn't copy — select the value manually.");
    }
  };

  return (
    <div className="wl-card-solid p-6 mt-6" data-testid="vr-token-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--brand-tint)", color: "var(--brand)" }}>
          <Key size={18} />
        </div>
        <div>
          <div className="font-display text-lg font-semibold">VR pipeline integration</div>
          <div className="text-xs text-[var(--text-secondary)] mt-0.5">Shared bearer token used by the Value Realisation Apps Script.</div>
        </div>
      </div>

      {info ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-xs uppercase tracking-wide text-[var(--text-secondary)]">Current token</div>
            <code className="font-mono text-xs px-2 py-1 rounded bg-[var(--bg-surface-warm)] border border-[var(--border-default)]" data-testid="vr-token-value">
              {revealed ?? info.token}
            </code>
            {!revealed && (
              <button onClick={reveal} disabled={busy} className="wl-btn-secondary text-xs inline-flex items-center gap-1.5" data-testid="vr-token-reveal-btn">
                <Eye size={12} /> Reveal once
              </button>
            )}
            {revealed && (
              <>
                <button onClick={() => copy(revealed)} className="wl-btn-secondary text-xs inline-flex items-center gap-1.5" data-testid="vr-token-copy-btn">
                  <Copy size={12} /> Copy
                </button>
                <button onClick={() => setRevealed(null)} className="wl-btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="vr-token-hide-btn">
                  <EyeOff size={12} /> Hide
                </button>
              </>
            )}
            <button onClick={regenerate} disabled={busy} className="wl-btn-ghost text-xs inline-flex items-center gap-1.5 ml-auto" data-testid="vr-token-rotate-btn">
              <RefreshCw size={12} /> Regenerate
            </button>
          </div>
          {info.rotated_at && (
            <div className="text-[11px] text-[var(--text-secondary)]">Last rotated · {new Date(info.rotated_at).toLocaleString()}</div>
          )}
          <details className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface-warm)]">
            <summary className="cursor-pointer px-3.5 py-2.5 text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)] flex items-center gap-2">
              <Info size={13} /> How the Apps Script uses this token
            </summary>
            <div className="px-3.5 pb-3.5 pt-1 text-xs text-[var(--text-primary)] leading-relaxed space-y-2">
              <p>The Apps Script sends this token in the <code className="font-mono text-[11px]">Authorization: Bearer &lt;token&gt;</code> header on every request. Two endpoints are protected by it:</p>
              <ul className="list-disc pl-4 space-y-1">
                <li><code className="font-mono text-[11px]">GET /api/admin/campaigns-for-vr</code> — pull the latest active / last-90-day campaigns.</li>
                <li><code className="font-mono text-[11px]">POST /api/admin/campaigns/{`{portal_id}`}/vr</code> — push the approved VR sheet URL back into the portal.</li>
              </ul>
              <p>To rotate: click Regenerate, copy the new token, then update <code className="font-mono text-[11px]">Script Properties → VR_SYNC_TOKEN</code> inside Apps Script. The old token stops working immediately.</p>
            </div>
          </details>
        </div>
      ) : (
        <div className="text-xs text-[var(--text-secondary)]">Loading…</div>
      )}
    </div>
  );
}
